import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Formik } from 'formik';
import { validate } from '../../../../helpers/validation';
import { useTranslation } from 'react-i18next';
import Hover from '../../../../components/Hover';
import { Button } from '../../../../components/Buttons';
import { Input, ValidationState } from '../../../../components/Forms';
import BasicInfoItem from '../../components/BasicInfoItem';
import Icon from '../../../../components/Icons';
import { colors } from '../../../../styles/constants';


type AttributeInputProps ={
    validationFunction?: (message: string) => (value: string) => string | undefined;
    validationText?: string;
    label: string;
    value: string;
    name: string;
    placeholder: string;
    onChange: (value: string) => void;
    onRemove: () => void;
    initialIsEditing: boolean;
    inlineLabel: boolean;
    error?: string;
};

const AttributeInput = ({
    validationFunction,
    validationText,
    label,
    value,
    name,
    onChange,
    placeholder,
    onRemove,
    initialIsEditing,
    inlineLabel,
    error,
}: AttributeInputProps) => {
    const { t } = useTranslation();

    const [isEditing, setIsEditing] = useState(initialIsEditing || false);
    const initialValues = { [name]: value };
    return (
        <Wide>
            <Hover component="div">
                {(hovering) =>
                    isEditing ? (
                        <Formik
                            initialValues={initialValues}
                            validate={(values) => {
                                const errors: { [key: string]: string } = {};
                                if (!validationFunction || !validationText) return errors;
                                return validate(name, validationFunction(validationText), values, errors);
                            }}
                            onSubmit={(values) => {
                                onChange(values[name]);
                                setIsEditing(false);
                            }}
                        >
                            {({
                                values,
                                errors,
                                handleChange,
                                handleBlur,
                                handleSubmit,
                                touched,
                                resetForm,
                            }) => {
                                return (
                                    <InputForm onSubmit={handleSubmit}>
                                        <InputGroup>
                                            {inlineLabel && <Label>{label}</Label>}
                                            <Input
                                                autoFocus={true}
                                                placeholder={placeholder}
                                                name={name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values[name]}
                                                label={inlineLabel ? undefined: label}
                                                validation={
                                                    error ? { state: ValidationState.Invalid, message: error } : undefined
                                                }
                                            />
                                            <Button
                                                type="button"
                                                className={'tiny alt'}
                                                onClick={() => {
                                                    resetForm();
                                                    setIsEditing(false);
                                                }}
                                                color="secondary"
                                            >
                                                {t('customers.fields.form.cancel')}
                                            </Button>
                                            <Button
                                                className={'tiny'}
                                                type="submit"
                                                color="primary"
                                            >
                                                {t('customers.fields.form.save')}
                                            </Button>
                                        </InputGroup>
                                        <Button type="button" onClick={onRemove} className="alt tiny" title={t('customers.customer.attributes.remove')}>
                                            <Icon icon="close_small" fill={colors.invalid} />
                                        </Button>
                                    </InputForm>
                                );
                            }}
                        </Formik>
                    ) : (
                        <BasicInfoItem
                            value={value}
                            displayValue={value}
                            notSetValue={'N/A'}
                            isHovering={hovering}
                            label={label}
                            onClick={() => setIsEditing(true)}
                        />
                    )
                }
            </Hover>
        </Wide>
    );
};

export default AttributeInput;


const Wide = styled.div`
    width: 100%;
`;

const InputForm = styled.form`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const InputGroup = styled.div`
    display: flex;
    align-items: center;
    gap:8px;
`;

const Label = styled.div`
    font-weight: 600;
    margin-right: 8px;
`;


const ErrorMessage = styled.span`
    font-size: 14px;
    color: #c71b1b;
    text-align: center;
    display: block;
    margin-top: 3px;
`;
